// QR Kiosk v2 — KV2-8 guest landing (route /public-v2/guest, ANONYMOUS, outside the
// auth guard). Reads the magic-link token from the URL FRAGMENT (#code=… — never a
// query string, SEC-REQ-B6-2), redeems it, stores the returned guest token via the
// existing kiosk mechanism, then navigates into the kiosk as the guest. No #code →
// PIN entry. Self-contained shell (imports the token sheet + wraps the scoped root),
// because KioskV2Layout only mounts behind the auth guard.
import "../kiosk-v2-tokens.css";

import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import { useBreakpoint } from "../../../shared/hooks/useBreakpoint";
import useTranslation from "../../../shared/hooks/useTranslation";
import { refreshAccount } from "../../../account/accountSlice";
import { Glyph, GD } from "../shared/glyphs";
import { redeemMagicLink, GuestRedeemResult, GuestRedeemErrorKind, guestRedeemErrorKind } from "./guestAccessApi";
import { storeGuestSession } from "./guestSession";
import GuestPinForm from "./GuestPinForm";

type Mode = "redeeming" | "error" | "pin";

function readFragmentCode(): string | null {
  const hash = window.location.hash || "";
  if (!hash) return null;
  return new URLSearchParams(hash.replace(/^#/, "")).get("code");
}

function clearFragment() {
  // Drop the token from the URL + history without a navigation (no #code left behind).
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
}

function Brand() {
  return (
    <span style={{ fontWeight: 800, fontSize: 17, letterSpacing: "0.02em", color: "var(--ink)", display: "inline-flex", alignItems: "center" }}>
      MACHINE
      <b style={{ color: "var(--card)", background: "var(--ink)", padding: "2px 5px", borderRadius: 4, marginLeft: 1, fontSize: 14, fontWeight: 800 }}>ID</b>
    </span>
  );
}

export function GuestLandingV2() {
  const { translate } = useTranslation("public");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isPhone, isTablet } = useBreakpoint();

  const params = new URLSearchParams(location.search);
  const prefillMachine = params.get("machine") || undefined;
  const expired = params.get("expired") === "1";

  const codeRef = useRef<string | null>(null);
  if (codeRef.current === null) codeRef.current = readFragmentCode();
  const code = codeRef.current;

  const [mode, setMode] = useState<Mode>(code ? "redeeming" : "pin");
  const [errKind, setErrKind] = useState<GuestRedeemErrorKind | null>(null);

  const enterKiosk = (result: GuestRedeemResult, redeemToken?: string) => {
    storeGuestSession(result, redeemToken);
    dispatch(refreshAccount(null));
    navigate(`/public-v2/machines/${encodeURIComponent(result.machineInternalNumber)}`, { replace: true });
  };

  const runMagicLink = async (c: string) => {
    setMode("redeeming");
    setErrKind(null);
    try {
      const result = await redeemMagicLink(c);
      enterKiosk(result, c);
    } catch (err) {
      setErrKind(guestRedeemErrorKind(err));
      setMode("error");
    }
  };

  // Redeem the fragment token once, then strip it from the URL immediately.
  useEffect(() => {
    if (!code) return;
    clearFragment();
    void runMagicLink(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columnMaxWidth: number | string = isPhone ? "100%" : isTablet ? 460 : 460;

  const errorTitle = errKind === "rateLimited"
    ? translate("kioskV2GuestRateLimitedTitle")
    : errKind === "network"
    ? translate("kioskV2GuestNetworkTitle")
    : translate("kioskV2GuestInvalidTitle");
  const errorHint = errKind === "rateLimited"
    ? translate("kioskV2GuestRateLimitedHint")
    : errKind === "network"
    ? translate("kioskV2GuestNetworkHint")
    : translate("kioskV2GuestInvalidHint");

  return (
    <div
      className="qr-kiosk-v2-root p-machineid tier-guest"
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "var(--bg)", width: "100%" }}
    >
      <div style={{ width: "100%", maxWidth: columnMaxWidth, margin: "0 auto", padding: "0 16px", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ height: 56, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Brand />
        </div>

        <div
          className="kv2-fade"
          style={{
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 16,
            boxShadow: "var(--shadow)",
            padding: "24px 20px 22px",
            marginTop: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--tier-accent)", flexShrink: 0 }} />
            <span className="eyebrow" style={{ color: "var(--tier-accent-hov)" }}>{translate("kioskV2GuestBannerLabel")}</span>
          </div>

          {mode === "redeeming" && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, padding: "28px 0 22px" }}>
              <div className="kv2-spinner" />
              <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink-2)" }}>{translate("kioskV2GuestVerifying")}</div>
            </div>
          )}

          {mode === "error" && (
            <div style={{ paddingTop: 6 }}>
              <div style={{ color: errKind === "rateLimited" ? "var(--amber)" : "var(--red)", display: "flex" }}>
                <Glyph d={GD.warn} size={26} />
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)", marginTop: 10, lineHeight: 1.35 }}>{errorTitle}</div>
              <div style={{ fontSize: 13.5, color: "var(--muted)", marginTop: 6, lineHeight: 1.5 }}>{errorHint}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 18 }}>
                {code && (
                  <button
                    type="button"
                    onClick={() => void runMagicLink(code)}
                    style={{
                      height: 50, borderRadius: 12, border: "1px solid var(--tier-accent)",
                      background: "var(--tier-accent)", color: "var(--tier-accent-fg)",
                      fontFamily: "inherit", fontSize: 15, fontWeight: 700, cursor: "pointer",
                    }}
                  >
                    {translate("kioskV2GuestRetry")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setMode("pin"); setErrKind(null); }}
                  style={{
                    height: 48, borderRadius: 12, border: "1px solid var(--border)",
                    background: "var(--card)", color: "var(--ink-2)",
                    fontFamily: "inherit", fontSize: 14.5, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  {translate("kioskV2GuestUsePinInstead")}
                </button>
              </div>
            </div>
          )}

          {mode === "pin" && (
            <div style={{ paddingTop: 6 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ink)", lineHeight: 1.3 }}>
                {expired ? translate("kioskV2GuestExpiredTitle") : translate("kioskV2GuestPinTitle")}
              </div>
              <div style={{ fontSize: 13.5, color: "var(--muted)", marginTop: 6, marginBottom: 18, lineHeight: 1.5 }}>
                {expired ? translate("kioskV2GuestExpiredHint") : translate("kioskV2GuestPinHint")}
              </div>
              <GuestPinForm prefillMachine={prefillMachine} onSuccess={(result) => enterKiosk(result)} />
            </div>
          )}
        </div>

        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 11.5, color: "var(--faint)", textAlign: "center", padding: "16px 0 calc(16px + env(safe-area-inset-bottom))", lineHeight: 1.5 }}>
          {translate("kioskV2GuestFooterNote")}
        </div>
      </div>
    </div>
  );
}

export default GuestLandingV2;
